import { useState, useEffect } from "react";
import { toast } from "sonner";
import SEO from "../components/common/SEO";
import AdminManageDialog from "../components/admin/AdminManageDialog";
import AdminCMS from "../components/admin/AdminCMS";
import AdminAvailability from "../components/admin/AdminAvailability";
import { adminSummary, adminList, adminUpdateStatus, adminSendDigest, bulkAction, downloadResumesZip, API } from "../lib/api";

const BULK_STATUSES = ["Under Review", "Shortlisted", "Interviewing", "Offer", "Hired", "Not Selected"];

const STAGES = ["Submitted", "Under Review", "Shortlisted", "Interviewing", "Offer", "Hired", "Not Selected", "Withdrawn"];

const ADMIN_KEY_STORAGE = "amvi_admin_key";

const TABS = [
  { key: "leads", label: "Contact Leads" },
  { key: "staffing-requirements", label: "Staffing Requirements" },
  { key: "applications", label: "Applications" },
];

export default function Admin() {
  const [key, setKey] = useState("");
  const [authed, setAuthed] = useState(false);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("leads");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [manageApp, setManageApp] = useState(null);
  const [fStatus, setFStatus] = useState("");
  const [fRole, setFRole] = useState("");
  const [fWorkAuth, setFWorkAuth] = useState("");
  const [view, setView] = useState("submissions");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("");

  const toggleSel = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  const clearSel = () => setSelected(new Set());

  const applyBulkStatus = async () => {
    const ids = [...selected];
    if (ids.length === 0 || !bulkStatus) return;
    setBulkBusy(true);
    try {
      const res = await bulkAction(ids, bulkStatus, false, key);
      setRows((rs) => rs.map((r) => (selected.has(r.id) ? { ...r, status: bulkStatus } : r)));
      clearSel();
      toast.success(`Updated ${res.updated} to ${bulkStatus}`);
      setBulkStatus("");
    } catch {
      toast.error("Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const downloadResumes = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const blob = await downloadResumesZip(ids, key);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "amvi-resumes.zip";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Resumes downloaded");
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "No resumes for the selected applications" : "Download failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkInterview = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await bulkAction(ids, "Interviewing", true, key);
      setRows((rs) => rs.map((r) => (selected.has(r.id) ? { ...r, status: "Interviewing" } : r)));
      clearSel();
      toast.success(`Moved ${res.updated} to Interviewing`, {
        description: res.slots_generated
          ? `Invites sent with ${res.slots_generated} proposed time(s) each.`
          : "No weekly availability set yet — add it under the Availability tab to auto-send times.",
      });
    } catch {
      toast.error("Bulk action failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const load = async (which) => {
    setLoading(true);
    clearSel();
    try {
      const data = await adminList(which, key);
      setRows(data);
      setTab(which);
    } catch (e) {
      setError("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  const changeStatus = async (row, newStatus) => {
    if (newStatus === row.status) return;
    const note = window.prompt("Optional note to include in the candidate's email:", "") || "";
    try {
      await adminUpdateStatus(row.id, { status: newStatus, note }, key);
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, status: newStatus } : r)));
      toast.success(`Status updated to ${newStatus}`, { description: "The candidate has been notified by email." });
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const s = await adminSummary(key);
      setSummary(s);
      setAuthed(true);
      localStorage.setItem(ADMIN_KEY_STORAGE, key);
      await load("leads");
    } catch (e) {
      setError("Invalid admin key.");
    }
  };

  const signOut = () => {
    localStorage.removeItem(ADMIN_KEY_STORAGE);
    setKey("");
    setAuthed(false);
    setSummary(null);
    setRows([]);
    setView("submissions");
    clearSel();
  };

  useEffect(() => {
    const stored = localStorage.getItem(ADMIN_KEY_STORAGE);
    if (!stored) return;
    (async () => {
      try {
        const s = await adminSummary(stored);
        setKey(stored);
        setSummary(s);
        setAuthed(true);
        const data = await adminList("leads", stored);
        setRows(data);
        setTab("leads");
      } catch {
        localStorage.removeItem(ADMIN_KEY_STORAGE);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen px-6 pt-32 pb-24">
      <SEO title="Admin" path="/admin" />
      <div className="mx-auto max-w-6xl">
        {!authed ? (
          <form onSubmit={login} className="mx-auto max-w-sm rounded-2xl border border-white/10 bg-ink-800/50 p-8" data-testid="admin-login">
            <h1 className="font-heading text-2xl font-bold text-white">Admin Access</h1>
            <p className="mt-2 text-sm text-slate-400">Enter your admin key to view submissions.</p>
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Admin key"
              className="mt-6 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white outline-none focus:border-sky-400/70"
              data-testid="admin-key-input"
            />
            {error && <p className="mt-3 text-sm text-red-400">{error}</p>}
            <button type="submit" className="mt-4 w-full rounded-full bg-sky-400 px-6 py-3 text-sm font-medium text-ink-900 hover:bg-sky-300" data-testid="admin-login-btn">
              Sign In
            </button>
          </form>
        ) : (
          <div>
            <div className="mb-8 flex flex-wrap gap-2" data-testid="admin-view-nav">
              <button
                onClick={() => setView("submissions")}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${view === "submissions" ? "border-sky-400 bg-sky-400 text-ink-900" : "border-white/10 text-slate-300 hover:text-white"}`}
                data-testid="admin-view-submissions"
              >
                Submissions
              </button>
              <button
                onClick={() => setView("content")}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${view === "content" ? "border-sky-400 bg-sky-400 text-ink-900" : "border-white/10 text-slate-300 hover:text-white"}`}
                data-testid="admin-view-content"
              >
                Content (CMS)
              </button>
              <button
                onClick={() => setView("availability")}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${view === "availability" ? "border-sky-400 bg-sky-400 text-ink-900" : "border-white/10 text-slate-300 hover:text-white"}`}
                data-testid="admin-view-availability"
              >
                Availability
              </button>
              <button
                onClick={signOut}
                className="ml-auto rounded-full border border-white/10 px-4 py-2 text-sm font-medium text-slate-300 transition-colors hover:border-red-400/50 hover:text-red-300"
                data-testid="admin-sign-out"
              >
                Sign out
              </button>
            </div>
            {view === "content" ? (
              <AdminCMS adminKey={key} />
            ) : view === "availability" ? (
              <AdminAvailability adminKey={key} />
            ) : (
            <div>
            <h1 className="font-heading text-3xl font-bold text-white">Submissions Dashboard</h1>
            <button
              onClick={async () => {
                try {
                  const r = await adminSendDigest(key);
                  toast.success("Weekly digest sent", { description: `${r.new_applications} new · ${r.status_changes} status changes` });
                } catch {
                  toast.error("Could not send digest");
                }
              }}
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-white transition-colors hover:border-sky-400/60 hover:text-sky-400"
              data-testid="admin-send-digest"
            >
              Send Weekly Digest Now
            </button>
            {summary && (
              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                {TABS.map((t) => (
                  <div key={t.key} className="rounded-2xl border border-white/10 bg-ink-800/50 p-6">
                    <div className="text-xs uppercase tracking-[0.15em] text-slate-500">{t.label}</div>
                    <div className="mt-2 font-heading text-4xl font-bold text-sky-400">{summary[t.key.replace(/-/g, "_")] ?? 0}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-10 flex flex-wrap gap-2">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => load(t.key)}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                    tab === t.key ? "border-sky-400 bg-sky-400 text-ink-900" : "border-white/10 text-slate-300 hover:text-white"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "applications" && rows.length > 0 && (
              <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="admin-filters">
                <select
                  value={fRole}
                  onChange={(e) => setFRole(e.target.value)}
                  className="rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70"
                  data-testid="admin-filter-position"
                >
                  <option value="" className="bg-ink-900">All positions</option>
                  {Array.from(new Set(rows.map((r) => r.role || "General Application"))).map((p) => (
                    <option key={p} value={p} className="bg-ink-900">{p}</option>
                  ))}
                </select>
                <select
                  value={fStatus}
                  onChange={(e) => setFStatus(e.target.value)}
                  className="rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70"
                  data-testid="admin-filter-status"
                >
                  <option value="" className="bg-ink-900">All statuses</option>
                  {STAGES.map((s) => (
                    <option key={s} value={s} className="bg-ink-900">{s}</option>
                  ))}
                </select>
                <select
                  value={fWorkAuth}
                  onChange={(e) => setFWorkAuth(e.target.value)}
                  className="rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70"
                  data-testid="admin-filter-work-auth"
                >
                  <option value="" className="bg-ink-900">All work authorizations</option>
                  {Array.from(new Set(rows.map((r) => r.work_authorization).filter(Boolean))).map((w) => (
                    <option key={w} value={w} className="bg-ink-900">{w}</option>
                  ))}
                </select>
                {(fRole || fStatus || fWorkAuth) && (
                  <button onClick={() => { setFRole(""); setFStatus(""); setFWorkAuth(""); }} className="text-sm text-slate-400 hover:text-white" data-testid="admin-filter-clear">Clear</button>
                )}
              </div>
            )}

            {tab === "applications" && selected.size > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-sky-400/30 bg-sky-400/10 px-4 py-3" data-testid="admin-bulk-bar">
                <span className="text-sm font-medium text-sky-200" data-testid="admin-bulk-count">{selected.size} selected</span>
                <button
                  onClick={runBulkInterview}
                  disabled={bulkBusy}
                  className="rounded-full bg-sky-400 px-4 py-2 text-sm font-medium text-ink-900 hover:bg-sky-300 disabled:opacity-60"
                  data-testid="admin-bulk-interview"
                >
                  {bulkBusy ? "Working..." : "Move to Interviewing & send invites"}
                </button>
                <div className="flex items-center gap-2">
                  <select
                    value={bulkStatus}
                    onChange={(e) => setBulkStatus(e.target.value)}
                    className="rounded-full border border-white/15 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70"
                    data-testid="admin-bulk-status-select"
                  >
                    <option value="" className="bg-ink-900">Set status…</option>
                    {BULK_STATUSES.map((s) => (
                      <option key={s} value={s} className="bg-ink-900">{s}</option>
                    ))}
                  </select>
                  <button
                    onClick={applyBulkStatus}
                    disabled={bulkBusy || !bulkStatus}
                    className="rounded-full border border-white/20 px-4 py-2 text-sm font-medium text-white hover:border-sky-400/60 hover:text-sky-300 disabled:opacity-50"
                    data-testid="admin-bulk-apply-status"
                  >
                    Apply
                  </button>
                </div>
                <button
                  onClick={downloadResumes}
                  disabled={bulkBusy}
                  className="rounded-full border border-white/20 px-4 py-2 text-sm font-medium text-white hover:border-sky-400/60 hover:text-sky-300 disabled:opacity-60"
                  data-testid="admin-bulk-resumes"
                >
                  Download resumes
                </button>
                <button onClick={clearSel} className="text-sm text-slate-400 hover:text-white" data-testid="admin-bulk-clear">Clear selection</button>
              </div>
            )}

            <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10">
              {loading ? (
                <div className="p-8 text-slate-400">Loading...</div>
              ) : rows.length === 0 ? (
                <div className="p-8 text-slate-400">No submissions yet.</div>
              ) : tab === "applications" ? (
                <table className="w-full text-left text-sm">
                  <thead className="bg-white/5 text-xs uppercase tracking-wide text-slate-400">
                    <tr>
                      <th className="px-4 py-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-sky-400"
                          data-testid="admin-select-all"
                          checked={(() => {
                            const vis = rows.filter((r) => (!fRole || (r.role || "General Application") === fRole) && (!fStatus || (r.status || "Submitted") === fStatus) && (!fWorkAuth || (r.work_authorization || "") === fWorkAuth));
                            return vis.length > 0 && vis.every((r) => selected.has(r.id));
                          })()}
                          onChange={(e) => {
                            const vis = rows.filter((r) => (!fRole || (r.role || "General Application") === fRole) && (!fStatus || (r.status || "Submitted") === fStatus) && (!fWorkAuth || (r.work_authorization || "") === fWorkAuth));
                            setSelected((s) => {
                              const n = new Set(s);
                              if (e.target.checked) vis.forEach((r) => n.add(r.id));
                              else vis.forEach((r) => n.delete(r.id));
                              return n;
                            });
                          }}
                        />
                      </th>
                      <th className="whitespace-nowrap px-4 py-3">Name</th>
                      <th className="whitespace-nowrap px-4 py-3">Email</th>
                      <th className="whitespace-nowrap px-4 py-3">Position</th>
                      <th className="whitespace-nowrap px-4 py-3">Skill</th>
                      <th className="whitespace-nowrap px-4 py-3">Work Auth</th>
                      <th className="whitespace-nowrap px-4 py-3">Submitted</th>
                      <th className="whitespace-nowrap px-4 py-3">Resume</th>
                      <th className="whitespace-nowrap px-4 py-3">Status (update)</th>
                      <th className="whitespace-nowrap px-4 py-3">Manage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows
                      .filter((r) => (!fRole || (r.role || "General Application") === fRole) && (!fStatus || (r.status || "Submitted") === fStatus) && (!fWorkAuth || (r.work_authorization || "") === fWorkAuth))
                      .map((r) => (
                      <tr key={r.id} className="border-t border-white/5 text-slate-300">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-sky-400"
                            checked={selected.has(r.id)}
                            onChange={() => toggleSel(r.id)}
                            data-testid={`admin-select-${r.id}`}
                          />
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-white">
                          <div className="flex items-center gap-2">
                            {r.name}
                            {r.reschedule_requested && (
                              <span className="rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-300" data-testid={`admin-reschedule-badge-${r.id}`}>
                                Reschedule requested
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">{r.email}</td>
                        <td className="whitespace-nowrap px-4 py-3">{r.role || "General Application"}</td>
                        <td className="whitespace-nowrap px-4 py-3">{r.primary_skill}</td>
                        <td className="whitespace-nowrap px-4 py-3">
                          {r.work_authorization ? (
                            <span className="rounded-full border border-emerald-400/25 bg-emerald-400/10 px-2 py-0.5 text-[11px] font-medium text-emerald-300">{r.work_authorization}</span>
                          ) : (<span className="text-slate-600">—</span>)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">{(r.created_at || "").slice(0, 10)}</td>
                        <td className="whitespace-nowrap px-4 py-3">
                          {r.resume_path ? (
                            <a href={`${API}/admin/resume/${r.resume_path}?key=${encodeURIComponent(key)}`} target="_blank" rel="noopener noreferrer" className="text-sky-400 underline" data-testid="admin-resume-download">Download</a>
                          ) : ("—")}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <select
                            value={r.status || "Submitted"}
                            onChange={(e) => changeStatus(r, e.target.value)}
                            className="rounded-lg border border-white/10 bg-ink-900 px-3 py-1.5 text-sm text-white outline-none focus:border-sky-400/70"
                            data-testid={`admin-status-select-${r.id}`}
                          >
                            {STAGES.map((s) => (
                              <option key={s} value={s} className="bg-ink-900">{s}</option>
                            ))}
                          </select>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <button
                            onClick={() => setManageApp(r)}
                            className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-white transition-colors hover:border-sky-400/60 hover:text-sky-400"
                            data-testid={`admin-manage-${r.id}`}
                          >
                            Notes & History
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead className="bg-white/5 text-xs uppercase tracking-wide text-slate-400">
                    <tr>
                      {Object.keys(rows[0]).filter((k) => k !== "id").map((k) => (
                        <th key={k} className="whitespace-nowrap px-4 py-3">{k.replace(/_/g, " ")}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-t border-white/5 text-slate-300">
                        {Object.keys(r).filter((k) => k !== "id").map((k) => (
                          <td key={k} className="whitespace-nowrap px-4 py-3">{String(r[k])}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            </div>
            )}
          </div>
        )}
      </div>
      <AdminManageDialog
        app={manageApp}
        adminKey={key}
        open={!!manageApp}
        onOpenChange={(o) => !o && setManageApp(null)}
        onChanged={(id, upd) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...upd } : r)))}
      />
    </div>
  );
}
